import { useState } from 'react';
import { IconButton, Menu, MenuItem, Typography, Box } from '@mui/material';
import { LanguageType } from '../../@types';
import { useAvailableLanguages } from '../../hooks/useAvailableLanguages';
import { useLanguage } from '../../localization/LanguageInstanceProvider';
import { useTranslation } from 'react-i18next';
import { CompetitionFlag } from './CompetitionFlag';

const LANGUAGE_TO_COUNTRY = {
  en: 'United Kingdom',
  bn: 'Bangladesh',
  sr: 'Serbia',
  sq: 'Albania',
  fr: 'France',
  de: 'Germany',
  el: 'Greece',
} as const;

const styles = {
  iconButton: {
    height: 40,
    color: 'neutral.25',
  },
  menu: {
    background: 'background.paper',
  },
  menuItem: {
    height: { xs: 32, md: 36 },
    minHeight: 0,
    px: { xs: 1.5, md: 2 },
    '&.Mui-selected': {
      color: 'primary.main',
    },
    ':hover:not(.Mui-selected)': {
      color: 'primary.main',
    },
    '& span': {
      typography: { xs: 'body3', md: 'body2' },
      pl: { xs: 1.25, md: 0.5 },
    },
  },
};

const FlagIcon = ({ languageCode }: { languageCode: LanguageType }) => {
  const countryName = LANGUAGE_TO_COUNTRY[languageCode] || 'World';

  return <CompetitionFlag name={countryName} height='100%' width={24} sx={{ mr: { xs: 0, md: 1 } }} />;
};

const TranslateButton = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { i18n } = useTranslation();

  const { setSelectedLanguage } = useLanguage();
  const availableLanguages = useAvailableLanguages();
  const currentLanguage = i18n.language as LanguageType;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectLanguage = (languageKey: LanguageType) => {
    setSelectedLanguage(languageKey);
    handleClose();
  };

  return (
    <>
      <IconButton onClick={handleClick} sx={styles.iconButton}>
        <FlagIcon languageCode={currentLanguage} />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose} sx={styles.menu}>
        {availableLanguages.map((lang) => (
          <MenuItem
            key={lang.key}
            value={lang.key}
            onClick={() => handleSelectLanguage(lang.key)}
            sx={styles.menuItem}
            selected={lang.key === i18n.language}
          >
            <FlagIcon languageCode={lang.key as LanguageType} />
            <Typography component='span'>{lang.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default TranslateButton;
